/**
 * 벌점 검수 탭 (2026-08-02)
 *
 * 지금까지 자동 벌점은 계산되자마자 바로 부과됐다.
 * 앞으로는 **계산은 자동 / 발급·발송은 원장 확인 후**로 나눈다.
 *
 *   1차 (저녁시간 직전, 기본 17:50) → [예고 알림 발송]. 벌점은 아직 안 붙는다
 *   2차 (정규 마감, 기본 22:00)      → [벌점 부여 + 발송]
 *
 *   ⚠️ 목록을 채우는 스케줄러는 **매시 05분에만** 돈다(auto_penalty_scheduler).
 *   그래서 17:50 설정이면 실제로는 18:05, 토요일 마감 19:10 이면 20:05 에 올라온다.
 *   화면 안내는 설정 시각이 아니라 **실제로 올라오는 시각**을 보여준다(2026-09-26).
 *
 * '미정' 표시가 붙은 건은 유형을 고르기 전까지 발급되지 않는다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';

// 예고 알림톡(penalty_preview_001) 이 NHN 미등록이라 눌러도 실패한다.
// 카카오 승인이 끝나면 true 로만 바꾸면 열린다(백엔드는 이미 동작).
const NOTICE_READY = false;

interface ExceptionInfo {
  type: string;
  reason: string | null;
  status: string;
  status_label: string;
  penalty_exempt: boolean;
  receipt_required: boolean;
  receipt_checked: boolean;
}

interface QueueItem {
  id: string;
  student_id: string;
  student_name: string | null;
  student_phone: string | null;
  occurred_date: string;
  suggested_type: string;
  confirmed_type: string | null;
  final_type: string;
  type_label: string;
  points: number;
  reason: string;
  occurred_time: string | null;
  is_undecided: boolean;
  is_carried: boolean;
  receipt_pending: boolean;
  exceptions: ExceptionInfo[];
  undecided_reason: string | null;
  context: Record<string, any>;
  status: 'pending' | 'reviewed' | 'issued' | 'excluded';
  batch: string;
  staff_memo: string | null;
  pre_notified_at: string | null;
  issued_at: string | null;
}

interface Counts {
  pending: number;
  reviewed: number;
  issued: number;
  excluded: number;
  undecided: number;
  carried: number;
  students: number;
}

interface ReviewSettings {
  enabled: boolean;
  first_batch: string;
  notify_app: boolean;
  notify_alimtalk: boolean;
}

interface TypeOption {
  code: string;
  label: string;
}

/**
 * 설정 시각(HH:MM) → 목록이 실제로 채워지는 시각.
 * 스케줄러는 매시 05분에 '직전 1시간 안에 지난 시각'을 처리한다
 * (window_start < 설정시각 <= 실행시각). 05분 이하면 그 시의 05분, 넘으면 다음 시의 05분.
 */
const actualSweepTime = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const hour = m <= 5 ? h : (h + 1) % 24;
  return `${String(hour).padStart(2, '0')}:05`;
};

const getApiUrl = (path: string) => {
  const baseUrl = API_BASE_URL === '' ? '/api' : API_BASE_URL;
  return `${baseUrl}${path}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const todayStr = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + (now.getTimezoneOffset() * 60000) + 9 * 3600000);
  return kst.toISOString().slice(0, 10);
};

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: '미검수', cls: 'bg-amber-100 text-amber-800' },
  reviewed: { label: '검수완료', cls: 'bg-blue-100 text-blue-800' },
  issued: { label: '부여완료', cls: 'bg-green-100 text-green-800' },
  excluded: { label: '제외', cls: 'bg-gray-100 text-gray-500' },
};

export const PenaltyReviewTab: React.FC = () => {
  const [date, setDate] = useState<string>(todayStr());
  const [items, setItems] = useState<QueueItem[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [settings, setSettings] = useState<ReviewSettings | null>(null);
  const [typeOptions, setTypeOptions] = useState<TypeOption[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [memoOpen, setMemoOpen] = useState<string | null>(null);
  const [memoText, setMemoText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`/penalties/review?target_date=${date}`), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`조회 실패 (${res.status})`);
      const data = await res.json();
      setItems(data.items || []);
      setCounts(data.counts || null);
      setSettings(data.settings || null);
      setTypeOptions(data.selectable_types || []);
      setSelected(new Set());
    } catch (e: any) {
      setMessage(e.message || '조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const patchItem = async (id: string, body: Record<string, any>) => {
    setBusy(true);
    try {
      const res = await fetch(getApiUrl(`/penalties/review/${id}`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`처리 실패 (${res.status})`);
      await load();
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  const excludeItem = async (id: string) => {
    setBusy(true);
    try {
      const res = await fetch(getApiUrl(`/penalties/review/${id}/exclude`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: '원장 확인 후 제외' }),
      });
      if (!res.ok) throw new Error(`제외 실패 (${res.status})`);
      await load();
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  // 🔴 [벌점 부여 + 발송] 을 누르기 **전에** 미정 건을 알려주는 확인창. (2026-08-29)
  //    종전엔 누른 뒤에야 "미정 N건 건너뜀" 이 떴다. 원장은 이미 눌렀으니
  //    "됐겠지" 하고 화면을 나갔고, 그 건들은 다음 날까지 이월됐다
  //    (2026-08-28 대전월평점 문의 — 6건이 그대로 남아 있었다).
  //    화면 위쪽 빨간 안내는 목록을 스크롤하면 화면 밖으로 나가 안 보인다.
  //    ⚠️ window.confirm 은 매장 환경에 따라 차단돼 "눌러도 반응 없음" 이 되므로 쓰지 않는다.
  const [confirmIssue, setConfirmIssue] = useState(false);

  const runBulk = async (path: string, label: string) => {
    setBusy(true);
    setMessage(null);
    try {
      const ids = selected.size > 0 ? Array.from(selected) : null;
      const res = await fetch(getApiUrl(`/penalties/review/${path}`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ item_ids: ids, target_date: date }),
      });
      if (!res.ok) throw new Error(`${label} 실패 (${res.status})`);
      const data = await res.json();
      if (path === 'notice') {
        setMessage(`예고 알림 ${data.notified ?? 0}건 발송했습니다. (벌점은 아직 부과되지 않았습니다)`);
      } else {
        const skipped = data.skipped ? ` / 미정 ${data.skipped}건은 건너뜀` : '';
        setMessage(`벌점 ${data.issued ?? 0}건 부여, 알림 ${data.notified ?? 0}건 발송${skipped}`);
      }
      await load();
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleSettings = async (patch: Partial<ReviewSettings>) => {
    setBusy(true);
    try {
      const res = await fetch(getApiUrl('/penalties/review/settings'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`설정 저장 실패 (${res.status})`);
      const data = await res.json();
      setSettings(data.settings);
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openItems = items.filter((i) => i.status === 'pending' || i.status === 'reviewed');
  const allOpenSelected = openItems.length > 0 && openItems.every((i) => selected.has(i.id));

  // 지금 버튼을 누르면 실제로 대상이 되는 건 (선택이 없으면 이 날짜 전체)
  const issueTargets = selected.size > 0 ? openItems.filter((i) => selected.has(i.id)) : openItems;
  const undecidedTargets = issueTargets.filter((i) => i.is_undecided);

  const toggleAll = () => {
    setSelected(allOpenSelected ? new Set() : new Set(openItems.map((i) => i.id)));
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <div className="space-y-4">
      {/* 안내 */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
        <p className="font-medium mb-1">벌점 계산은 자동, 부여·발송은 확인 후</p>
        <p className="text-blue-800">
          매일 <b>{actualSweepTime(settings?.first_batch || '17:50')}</b>쯤 지각·조퇴 건이 먼저 목록에 올라옵니다.
          이때는 벌점이 부과되지 않으며, 학생·학부모가 마감 전까지 사유서를 낼 수 있습니다.
          결석을 포함한 전체 목록은 <b>정규 마감 시각 뒤 첫 정각 5분</b>에 올라옵니다
          (예: 마감 22:00 → 22:05, 마감 19:10 → 20:05. 마감 시각은 [설정] → [운영 규칙] → 「자율학습」의 자율 시작 시각).
          남은 건을 확인하고 <b>벌점 부여 + 발송</b>을 누르면 그때 실제로 부과됩니다.
        </p>
        <p className="mt-1 text-xs text-blue-700">
          예고 알림톡은 <b>학생 본인</b>에게, 부과 안내는 <b>학부모</b>에게 갑니다.
          <span className="text-amber-700"> 미정</span> 표시된 건은 유형이 정해지기 전이라 예고에서 빠집니다
          (유형을 정하면 다음 예고에 포함됩니다).
        </p>
        {!NOTICE_READY && (
          <p className="mt-1 text-xs font-medium text-amber-800">
            ⚠️ <b>예고 알림 발송은 아직 준비 중</b>입니다 — 알림톡 템플릿 승인 후 열립니다.
            그때까지 <b>벌점 부여 + 발송</b>만 사용해 주세요.
          </p>
        )}
        <p className="mt-1 text-xs text-blue-700">
          확인하지 않고 넘어간 건은 <b>자동으로 부과되지 않고 계속 대기</b>합니다.
          지난 날짜의 미처리 건은 <span className="text-purple-700">이월</span> 표시로 오늘 목록에 함께 나옵니다.
        </p>
        <p className="mt-2 text-xs text-blue-700">
          발송 채널: {settings?.notify_app ? '앱 알림' : ''}
          {settings?.notify_app && settings?.notify_alimtalk ? ' + ' : ''}
          {settings?.notify_alimtalk ? '카카오 알림톡' : ''}
          {!settings?.notify_app && !settings?.notify_alimtalk ? '없음' : ''}
          {settings?.notify_alimtalk && (
            <span className="text-blue-600">
              {' '}(알림톡은 <b>연동 설정 → 알림톡</b>에서 &lsquo;벌점 부과 예정/부과 안내&rsquo;를 켜야 실제로 나갑니다)
            </span>
          )}
        </p>
      </div>

      {/* 상단 바 */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
        <button
          onClick={load}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          새로고침
        </button>

        <div className="flex-1" />

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!settings?.enabled}
            disabled={busy}
            onChange={(e) => toggleSettings({ enabled: e.target.checked })}
          />
          검수 방식 사용
          <span className="text-xs text-gray-400">(끄면 지금처럼 자동으로 바로 부과)</span>
        </label>
      </div>

      {/* 카운터 */}
      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: '미검수', value: counts.pending, cls: 'text-amber-600' },
            { label: '검수완료·미발송', value: counts.reviewed, cls: 'text-blue-600' },
            { label: '확인 필요(미정)', value: counts.undecided, cls: 'text-red-600' },
            { label: '이월(지난 날짜)', value: counts.carried ?? 0, cls: 'text-purple-600' },
            { label: '부여완료', value: counts.issued, cls: 'text-green-600' },
            { label: '대상 학생', value: counts.students, cls: 'text-gray-700' },
          ].map((c) => (
            <div key={c.label} className="p-3 bg-white border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-500">{c.label}</div>
              <div className={`text-2xl font-bold ${c.cls}`}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* 작업 버튼 */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() =>
            NOTICE_READY
              ? runBulk('notice', '예고 알림 발송')
              : setMessage('예고 알림 발송은 아직 준비 중입니다. 알림톡 템플릿 승인 후 열립니다.')
          }
          disabled={busy || openItems.length === 0}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-40"
        >
          예고 알림 발송 (벌점 부과 안 함)
        </button>
        <button
          onClick={() => {
            // 미정 건이 있으면 바로 처리하지 않고 먼저 알린다
            if (undecidedTargets.length > 0) setConfirmIssue(true);
            else runBulk('issue', '벌점 부여');
          }}
          disabled={busy || openItems.length === 0}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-40"
        >
          벌점 부여 + 발송
        </button>
        <span className="text-xs text-gray-500">
          {selected.size > 0 ? `선택한 ${selected.size}건에만 적용됩니다.` : '선택 없으면 이 날짜 전체에 적용됩니다.'}
        </span>
      </div>

      {confirmIssue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <div className="text-base font-bold text-gray-900">
              {undecidedTargets.length}건은 부여되지 않습니다
            </div>
            <div className="mt-2 text-sm text-gray-700">
              아래 학생은 <b>유형이 아직 정해지지 않아</b> 이번에 빠집니다.
            </div>
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              {undecidedTargets.map((i) => i.student_name || '이름없음').join(', ')}
            </div>
            <div className="mt-3 text-xs text-gray-600">
              표의 <b>확정 유형</b>에서 유형을 고르면 부여됩니다.
              벌점을 주지 않으실 경우에는 <b>제외</b>를 눌러 주세요.
              <br />
              그냥 진행하시면 이 건들은 <b>다음 날 목록으로 이월</b>됩니다.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setConfirmIssue(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                먼저 유형 정하기
              </button>
              <button
                onClick={() => { setConfirmIssue(false); runBulk('issue', '벌점 부여'); }}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                나머지만 부여
              </button>
            </div>
          </div>
        </div>
      )}

      {undecidedTargets.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <b>부여되지 않는 건이 {undecidedTargets.length}건 있습니다.</b>{' '}
          {undecidedTargets.map((i) => i.student_name || '이름없음').join(', ')} —
          유형이 아직 정해지지 않아 <b>벌점 부여 + 발송</b>을 눌러도 이 건은 빠집니다.
          <div className="mt-1 text-xs">
            아래 표의 <b>확정 유형</b>에서 유형을 고르면 부여됩니다.
            벌점을 주지 않을 경우에는 <b>제외</b>를 눌러 주세요.
          </div>
        </div>
      )}

      {message && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 flex justify-between">
          <span>{message}</span>
          <button onClick={() => setMessage(null)} className="text-gray-400">✕</button>
        </div>
      )}

      {/* 표 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={allOpenSelected} onChange={toggleAll} />
              </th>
              <th className="px-3 py-2 text-left">학생</th>
              <th className="px-3 py-2 text-left">확정 유형</th>
              <th className="px-3 py-2 text-left">점수</th>
              <th className="px-3 py-2 text-left">근거</th>
              <th className="px-3 py-2 text-left">상태</th>
              <th className="px-3 py-2 text-left">처리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">불러오는 중...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                이 날짜에 검수할 벌점이 없습니다.
              </td></tr>
            )}
            {!loading && items.map((it) => {
              const open = it.status === 'pending' || it.status === 'reviewed';
              const badge = STATUS_BADGE[it.status];
              return (
                <React.Fragment key={it.id}>
                  <tr className={it.is_undecided && open ? 'bg-red-50' : ''}>
                    <td className="px-3 py-2 align-top">
                      {open && (
                        <input
                          type="checkbox"
                          checked={selected.has(it.id)}
                          onChange={() => toggleOne(it.id)}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-gray-900">
                        {it.student_name || '-'}
                        {it.is_carried && (
                          <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[11px] align-middle">
                            이월 {it.occurred_date}
                          </span>
                        )}
                        {it.receipt_pending && (
                          <span
                            className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[11px] align-middle"
                            title="사유서 승인 시 「나중에 영수증 받을 건」으로 체크하셨고, 아직 영수증을 확인하지 않으셨습니다."
                          >
                            🧾 영수증 받기로 함
                          </span>
                        )}
                      </div>
                      {it.occurred_time && (
                        <div className="text-xs text-gray-500">{it.occurred_time}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {open ? (
                        <select
                          value={it.confirmed_type || (it.is_undecided ? '' : it.suggested_type)}
                          disabled={busy}
                          onChange={(e) => {
                            if (e.target.value) patchItem(it.id, { confirmed_type: e.target.value });
                          }}
                          className={`px-2 py-1 border rounded text-sm ${
                            it.is_undecided ? 'border-red-400 text-red-700 font-medium' : 'border-gray-300'
                          }`}
                        >
                          {it.is_undecided && <option value="">— 유형을 선택하세요 —</option>}
                          {typeOptions.map((t) => (
                            <option key={t.code} value={t.code}>{t.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span>{it.type_label}</span>
                      )}
                      {it.is_undecided && open && (
                        <div className="mt-1 text-xs text-red-600">
                          미정 — {it.undecided_reason}
                          <br />
                          유형을 고르기 전까지 부여되지 않습니다.
                          벌점을 주지 않을 경우 오른쪽 <b>제외</b>를 눌러 주세요.
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {open ? (
                        <input
                          type="number"
                          defaultValue={it.points}
                          disabled={busy}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!Number.isNaN(v) && v !== it.points) patchItem(it.id, { points: v });
                          }}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span>{it.points}점</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700 max-w-md">
                      <div>{it.reason}</div>
                      {(it.exceptions || []).map((ex, i) => (
                        <div
                          key={i}
                          className="mt-1 px-2 py-1 bg-blue-50 border border-blue-100 rounded text-xs text-blue-900"
                        >
                          <span className="font-medium">📄 {ex.type} 사유서</span>
                          <span className="text-blue-700"> · {ex.status_label}</span>
                          {ex.penalty_exempt && <span className="text-blue-700"> · 벌점 미부과로 승인</span>}
                          {ex.receipt_required && (
                            <span className="text-amber-700">
                              {ex.receipt_checked ? ' · 🧾 영수증 확인됨' : ' · 🧾 영수증 아직 안 냄'}
                            </span>
                          )}
                          {ex.reason && <div className="text-blue-800 mt-0.5">“{ex.reason}”</div>}
                        </div>
                      ))}
                      {it.staff_memo && (
                        <div className="mt-1 text-xs text-gray-500">메모: {it.staff_memo}</div>
                      )}
                      {it.pre_notified_at && it.status !== 'issued' && (
                        <div className="mt-1 text-xs text-amber-600">예고 알림 발송됨</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className={`px-2 py-1 rounded text-xs ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      {open && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => patchItem(it.id, { mark_reviewed: true })}
                            disabled={busy}
                            className="px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
                          >
                            검수
                          </button>
                          <button
                            onClick={() => excludeItem(it.id)}
                            disabled={busy}
                            className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                          >
                            제외
                          </button>
                          <button
                            onClick={() => {
                              setMemoOpen(memoOpen === it.id ? null : it.id);
                              setMemoText(it.staff_memo || '');
                            }}
                            className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                          >
                            메모
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {memoOpen === it.id && (
                    <tr>
                      <td colSpan={7} className="px-3 py-2 bg-gray-50">
                        <div className="flex gap-2">
                          <input
                            value={memoText}
                            onChange={(e) => setMemoText(e.target.value)}
                            placeholder="근무자 메모 (예: 학원 수업으로 조퇴, 학부모 전화 확인)"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                          />
                          <button
                            onClick={async () => {
                              await patchItem(it.id, { staff_memo: memoText });
                              setMemoOpen(null);
                            }}
                            className="px-3 py-2 bg-blue-600 text-white rounded text-sm"
                          >
                            저장
                          </button>
                          <button
                            onClick={() => setMemoOpen(null)}
                            className="px-3 py-2 border border-gray-300 rounded text-sm"
                          >
                            취소
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PenaltyReviewTab;
